/**
 * BuyerPassportSampleCard — Direction A toggle card.
 *
 * A self-contained, white card with a "Your view / Agent view" segmented
 * toggle. The two views share the same card shell but project different data:
 *
 *  - **Your view**  (default for the standalone surface; "Agent view" for the
 *    rail — see `defaultView` prop): buyer's own exact figures from the
 *    DashboardSnapshot + agent-preview metadata (name, SILL SCORE, exact $).
 *  - **Agent view**: the anonymized passport_preview projection from
 *    useAgentPreview — ranges, no name, no exact figures, no score.
 *
 * Field-leak contract: the Agent view NEVER renders the real display name or
 * exact dollar magnitudes. All financial data in the Agent view comes
 * exclusively from the `agentPreview` prop (passport_preview bucketed ranges).
 * The Your-view branch is the ONLY place exact figures appear.
 */

import { useState } from "react";
import { Eye, Shield } from "lucide-react";
import {
  PassportBadge,
  PassportCard,
  type PassportBadgeProps,
  type PassportBadgeTone,
} from "@sill/shared/components/marketplace/passport-card";
import type { AgentPreviewProfile } from "../../../hooks/useAgentPreview";
import type { DashboardSnapshot } from "../../../lib/dashboard";
// The agent-preview → card adapter lives in `lib/passport-card-data.ts`, the
// ONE place a consumer surface builds `PassportCardData` (2026-08-13 handoff
// H1). It used to be defined and exported here, which also made this file a
// component module exporting a non-component value — the Fast-Refresh rule in
// frontend/consumer/CLAUDE.md.
import { fromAgentPreview } from "../../../lib/passport-card-data";
import { cn } from "../../../lib/utils";
import { deriveBadgeTier } from "./tier-derivation";

// ──── Types ────────────────────────────────────────────────────────────────────

export type PassportView = "your" | "agent";

export interface BuyerPassportSampleCardProps {
  /** Full dashboard snapshot — Your view source. */
  snapshot: DashboardSnapshot;
  /** Agent-preview result — Agent view source (passport_preview projection). */
  agentPreview: AgentPreviewProfile | null;
  /** Which view is active on initial render. Defaults to "agent". */
  defaultView?: PassportView;
  className?: string;
}

// ──── Formatting helpers ───────────────────────────────────────────────────────

function fmtDollar(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000).toLocaleString()}K`;
  return `$${n.toLocaleString()}`;
}

/** Maps raw timeline enum tokens → human-readable label. Falls back to the
 *  raw string when the token is already human-readable (e.g. "6–12 months"). */
function fmtTimeline(raw: string | null | undefined): string {
  if (raw == null || raw === "") return "—";
  switch (raw) {
    case "0_3_months":  return "ASAP (0–3 months)";
    case "3_6_months":  return "3–6 months";
    case "6_12_months": return "6–12 months";
    case "12_plus":     return "Exploring (12+ months)";
    default:            return raw; // already humanized or free-text
  }
}

function fmtBuyerType(raw: string): string {
  const map: Record<string, string> = {
    first_time: "First home",
    "first-time": "First home",
    repeat: "Repeat buyer",
    move_up: "Move-up",
    investor: "Investor",
    downsizing: "Downsizing",
  };
  return map[raw] ?? raw;
}

function fmtHousing(raw: string): string {
  const fallback = raw.replace(/[_-]+/g, " ").trim();
  const map: Record<string, string> = {
    renting: "Renting",
    owning: "Homeowner",
    own: "Homeowner",
    own_selling: "Own selling",
    own_keeping: "Own keeping",
    with_family: "Living with family",
    family: "Living with family",
    other: "Other",
  };
  return map[raw.toLowerCase()] ?? fallback;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

// ──── Provenance pill — the shared PassportBadge from @sill/shared ────────────
// One pill component across Your view, Agent view, and the pro marketplace.

function tierToBadge(tier: string | null | undefined): PassportBadgeTone {
  if (tier === "connected" || tier === "pre_approval") return "bank";
  if (tier === "document") return "doc";
  return "self";
}

function currentInputTone(snapshot: DashboardSnapshot, field: string): PassportBadgeTone {
  const input = snapshot.financialInputs.find(
    (item) => item.field === field && item.present && item.freshness_state !== "stale",
  );
  return tierToBadge(input?.verification_tier);
}

function tierBadgeLabel(tone: PassportBadgeTone): string {
  if (tone === "bank") return "Verified";
  if (tone === "doc") return "Document";
  return "Self-reported";
}

// ──── FactCell — one cell of the 2×2 grid ─────────────────────────────────────

interface FactCellProps {
  eyebrow: string;
  value: React.ReactNode;
  valueClassName?: string;
}

function FactCell({ eyebrow, value, valueClassName }: FactCellProps) {
  return (
    <div className="flex flex-col gap-0.5 rounded-lg bg-muted/50 px-3 py-2.5">
      <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {eyebrow}
      </p>
      <p className={cn("text-sm font-semibold text-foreground", valueClassName)}>
        {value}
      </p>
    </div>
  );
}

// ──── ExactRow — a verified-financial row in Your view ────────────────────────

interface ExactRowProps {
  label: string;
  value: string;
  badge: PassportBadgeProps;
}

function ExactRow({ label, value, badge }: ExactRowProps) {
  return (
    <div
      className="flex items-center justify-between gap-3 py-3.5"
      data-testid="passport-exact-row"
    >
      <div className="min-w-0">
        <p className="text-xs font-semibold text-foreground">{label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{value}</p>
      </div>
      <PassportBadge {...badge} />
    </div>
  );
}

// ──── YourViewBody — the buyer's exact private view ───────────────────────────

interface YourViewBodyProps {
  snapshot: DashboardSnapshot;
  agentPreview: AgentPreviewProfile | null;
}

function YourViewBody({ snapshot, agentPreview }: YourViewBodyProps) {
  const { firstName, housingSituation, buyerType, readinessScore } = snapshot;
  const subtitle = [fmtBuyerType(buyerType), fmtHousing(housingSituation)]
    .filter(Boolean)
    .join(" · ");

  // SILL SCORE: prefer the readiness score from the snapshot. The agent-preview
  // hook may return a slightly different value (same backend source, different
  // call timing) — the snapshot is the buyer's own authoritative view.
  const score = readinessScore;

  // Down payment: exact savings (the raw dollar amount from the snapshot).
  const dp = snapshot.savings > 0 ? fmtDollar(snapshot.savings) : null;
  const dpTone = currentInputTone(snapshot, "savings");

  // Household income: exact.
  const income =
    snapshot.householdIncome > 0
      ? `${fmtDollar(snapshot.householdIncome)}/yr`
      : null;
  const incomeTone = currentInputTone(snapshot, "household_income");

  // Credit score: from creditBucket.
  const creditDisplay = agentPreview?.passportPreview?.creditBand
    ?? agentPreview?.creditScoreRange
    ?? snapshot.creditBucket;
  // Credit is reported by the buyer; there is no credit verification path.
  const creditTone: PassportBadgeTone = "self";

  // Monthly debts: exact.
  const debts =
    snapshot.monthlyDebts > 0
      ? `${fmtDollar(snapshot.monthlyDebts)}/mo`
      : null;
  const debtsTone = currentInputTone(snapshot, "monthly_debts");

  return (
    <div data-testid="your-view-body">
      {/* Identity block */}
      <div className="mx-0 rounded-xl bg-muted/50 px-4 py-3 mb-4">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="grid size-11 shrink-0 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground"
          >
            {initials(firstName ?? "Y")}
          </span>
          <div className="min-w-0 flex-1">
            <p
              className="text-sm font-semibold text-foreground"
              data-testid="your-view-name"
            >
              {firstName}
            </p>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
          <div className="shrink-0 text-right">
            <p
              className="text-4xl font-semibold tracking-[-0.04em] text-foreground leading-none"
              data-testid="your-view-score"
            >
              {score ?? "—"}
            </p>
            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              SILL SCORE
            </p>
          </div>
        </div>
      </div>

      {/* 2×2 fact grid */}
      <div
        className="grid grid-cols-2 gap-2 mb-4"
        data-testid="your-view-grid"
      >
        <FactCell eyebrow="Timeline" value={fmtTimeline(snapshot.timeline)} />
        <FactCell
          eyebrow="Price bracket"
          value={snapshot.targetPrice > 0 ? fmtDollar(snapshot.targetPrice) : "—"}
        />
        <FactCell
          eyebrow="Target area"
          value={snapshot.targetArea || "—"}
        />
        <FactCell
          eyebrow="Activity"
          value="Active"
          valueClassName="text-primary"
        />
      </div>

      {/* Financial profile rows carry their own source labels. */}
      <div className="mb-3">
        <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground mb-1">
          Financial Profile
        </p>
        <div className="divide-y divide-border">
          {dp && (
            <ExactRow
              label="Down payment"
              value={dp}
              badge={{ tone: dpTone, label: tierBadgeLabel(dpTone) }}
            />
          )}
          {income && (
            <ExactRow
              label="Household income"
              value={income}
              badge={{ tone: incomeTone, label: tierBadgeLabel(incomeTone) }}
            />
          )}
          {creditDisplay && (
            <ExactRow
              label="Credit score"
              value={creditDisplay}
              badge={{ tone: creditTone, label: tierBadgeLabel(creditTone) }}
            />
          )}
          {debts !== null && (
            <ExactRow
              label="Monthly debt"
              value={debts}
              badge={{ tone: debtsTone, label: tierBadgeLabel(debtsTone) }}
            />
          )}
        </div>
      </div>

      {/* Footer note */}
      <div
        className="rounded-lg bg-muted/40 px-3 py-2 flex items-start gap-2"
        data-testid="your-view-footer"
      >
        <Eye aria-hidden="true" className="size-3.5 shrink-0 mt-0.5 text-muted-foreground" />
        <p className="text-[11px] text-muted-foreground">
          Your private view — exact figures, name, and score.
        </p>
      </div>

    </div>
  );
}

// ──── AgentViewBody — the anonymized projection agents see ────────────────────

interface AgentViewBodyProps {
  agentPreview: AgentPreviewProfile | null;
  snapshot: DashboardSnapshot;
}

function AgentViewBody({ agentPreview, snapshot }: AgentViewBodyProps) {
  if (!agentPreview) {
    return (
      <div
        data-testid="agent-view-empty"
        className="py-6 text-center text-sm text-muted-foreground"
      >
        Complete your profile to see the agent-facing preview.
      </div>
    );
  }

  const passport = fromAgentPreview(agentPreview, snapshot);
  return (
    <div data-testid="agent-view-body">
      <PassportCard data={passport} mode="agent" />
    </div>
  );
}

// ──── BuyerPassportSampleCard ─────────────────────────────────────────────────

/**
 * BuyerPassportSampleCard — Direction A "toggle" card.
 *
 * Props:
 *  - snapshot: DashboardSnapshot (Your view source — exact figures)
 *  - agentPreview: AgentPreviewProfile | null (Agent view source — ranges only)
 *  - defaultView: "your" | "agent" (defaults to "agent" for the passport rail)
 *
 * Field-leak contract:
 *  - Agent view: ONLY renders from `agentPreview.passportPreview` (bucketed
 *    ranges from backend). Real name, exact $ figures, and SILL SCORE are
 *    structurally absent from this branch.
 *  - Your view: renders exact figures from `snapshot`. These are the buyer's
 *    own private data — appropriate for the buyer to see about themselves.
 */
export function BuyerPassportSampleCard({
  snapshot,
  agentPreview,
  defaultView = "agent",
  className,
}: BuyerPassportSampleCardProps) {
  const [view, setView] = useState<PassportView>(defaultView);

  // The tier pill shows the highest ACHIEVED verification tier (1/2/3), matching
  // the header badge and the ladder. It is NOT derived from the readiness score —
  // a high score never inflates the tier of a self-reported profile.
  const tierNum = deriveBadgeTier(snapshot);

  return (
    <div
      className={cn(
        "rounded-2xl bg-popover shadow-sm overflow-hidden",
        className,
      )}
      data-testid="buyer-passport-sample-card"
    >
      {/* Header row: "Buyer Passport" + tier pill */}
      <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-3">
        <p className="text-sm font-bold text-foreground">Buyer Passport</p>
        <span
          className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary"
          data-testid="passport-sample-tier-pill"
        >
          <Shield aria-hidden="true" className="size-3 shrink-0" strokeWidth={2.5} />
          TIER {tierNum}
        </span>
      </div>

      {/* Segmented toggle */}
      <div className="px-4 pb-3">
        <div
          className="flex rounded-lg bg-muted p-0.5"
          role="group"
          aria-label="Passport view selector"
        >
          <button
            type="button"
            role="radio"
            aria-checked={view === "your"}
            className={cn(
              "flex-1 rounded-md py-1.5 text-xs font-semibold transition-all",
              view === "your"
                ? "bg-popover border border-border shadow-sm text-foreground ring-1 ring-inset ring-border"
                : "text-muted-foreground",
            )}
            onClick={() => setView("your")}
            data-testid="passport-toggle-your"
          >
            Your view
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={view === "agent"}
            className={cn(
              "flex-1 rounded-md py-1.5 text-xs font-semibold transition-all",
              view === "agent"
                ? "bg-popover border border-border shadow-sm text-foreground ring-1 ring-inset ring-border"
                : "text-muted-foreground",
            )}
            onClick={() => setView("agent")}
            data-testid="passport-toggle-agent"
          >
            Agent view
          </button>
        </div>
      </div>

      {/* Card body — switches with the toggle */}
      <div className="px-4 pb-4">
        {view === "your" ? (
          <YourViewBody
            snapshot={snapshot}
            agentPreview={agentPreview}
          />
        ) : (
          <AgentViewBody
            agentPreview={agentPreview}
            snapshot={snapshot}
          />
        )}
      </div>
    </div>
  );
}
